/**
 * Configurações (F3c) — porte de src/pages/Configuracoes do app atual: empresa,
 * integração VHSys e "sobre", mais aparência e o banco local (Werc 2.0).
 * - VHSys é SÓ ONLINE: as credenciais ficam no SERVIDOR (não sincronizam para os
 *   computadores) e as chamadas vão direto à API.
 * - O backup local do Electron não existe aqui: os backups são feitos no servidor
 *   (docs/OPERACAO.md §1). Cada computador guarda uma cópia que sincroniza.
 */
import { createResource, createSignal, For, Show } from 'solid-js'
import { Button } from '@ui/components/Button'
import { Input } from '@ui/components/Input'
import type { UsuarioPublico, VhsysImportResult, VhsysTesteConexao } from '@shared/types'
import { api } from '../nucleo/cliente'
import type { InfoBanco } from '../nucleo/worker'
import { mensagemErro } from '../lib/erro'
import { preferencia, setPreferencia, type PreferenciaTema } from '../tema/tema'
import { Campo, confirmar, Erro, Etiqueta } from '../ui/kit'

const NOMES_TEMA: Record<PreferenciaTema, string> = { claro: 'Claro', escuro: 'Escuro', sistema: 'Igual ao sistema' }
const CAMPOS_EMPRESA: Array<{ chave: string; rotulo: string }> = [
  { chave: 'empresa.razao_social', rotulo: 'Razão social' },
  { chave: 'empresa.pais', rotulo: 'País' },
  { chave: 'moeda.padrao_venda', rotulo: 'Moeda padrão de venda' },
  { chave: 'moeda.padrao_custo', rotulo: 'Moeda padrão de custo' },
]

function Quadro(props: { titulo: string; children: any; extra?: any }) {
  return (
    <section class="rounded-xl border border-edge bg-panel p-5 flex flex-col gap-3">
      <div class="flex flex-wrap items-center gap-2">
        <h2 class="font-medium text-ink">{props.titulo}</h2>
        {props.extra}
      </div>
      {props.children}
    </section>
  )
}

export function Configuracoes(props: { banco: InfoBanco; usuario: UsuarioPublico }) {
  const admin = () => props.usuario.perfil === 'ADMIN'
  const [versao] = createResource(() => api.app.getVersion().catch(() => ''))
  return (
    <div class="p-4 sm:p-6 flex flex-col gap-6 max-w-3xl">
      <h1 class="text-xl font-semibold text-ink">Configurações</h1>
      <Show when={admin()}>
        <Empresa />
      </Show>
      <Quadro titulo="Aparência">
        <div class="flex flex-wrap gap-2">
          <For each={['claro', 'escuro', 'sistema'] as PreferenciaTema[]}>
            {(p) => (
              <Button variant={preferencia() === p ? 'accent' : 'secondary'} size="sm" onClick={() => setPreferencia(p)}>
                {NOMES_TEMA[p]}
              </Button>
            )}
          </For>
        </div>
      </Quadro>
      <Show when={admin()}>
        <Vhsys />
      </Show>
      <Quadro titulo="Banco local">
        <Show when={props.banco.persistente} fallback={<span class="text-sm text-warning">Banco temporário: os dados somem ao fechar</span>}>
          <span class="flex items-center gap-1.5 text-sm text-ink-muted">
            <span class="size-1.5 rounded-full bg-success" /> Banco local salvo neste computador
          </span>
        </Show>
        <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm text-ink-muted">
          <dt>Armazenamento</dt>
          <dd>{props.banco.armazenamento}</dd>
          <dt>SQLite</dt>
          <dd>{props.banco.versaoSqlite}</dd>
          <dt>Migrações</dt>
          <dd>{props.banco.migracoes}</dd>
          <dt>Tabelas</dt>
          <dd>{props.banco.tabelas}</dd>
        </dl>
        <p class="text-xs text-ink-placeholder">
          Este computador guarda uma cópia do banco e sincroniza com o servidor: dá para trabalhar offline, e as alterações sobem quando a conexão volta.
          Os <b>backups</b> são feitos no servidor, todo dia (não há backup manual por computador).
        </p>
      </Quadro>
      <Quadro titulo="Sobre">
        <div class="text-sm text-ink-muted">Werc Industrial 2.0 · núcleo {versao() || '—'}</div>
      </Quadro>
    </div>
  )
}

function Empresa() {
  const [config, { mutate }] = createResource(() => api.app.config().catch(() => ({}) as Record<string, string>))
  const [erro, setErro] = createSignal<string | null>(null)
  const [ok, setOk] = createSignal<string | null>(null)
  const [salvando, setSalvando] = createSignal(false)
  const salvar = async () => {
    const c = config()
    if (!c) return
    setErro(null)
    setOk(null)
    setSalvando(true)
    try {
      await api.app.setConfig(Object.fromEntries(CAMPOS_EMPRESA.map((x) => [x.chave, c[x.chave] ?? ''])))
      setOk('Dados da empresa salvos.')
    } catch (e) {
      setErro(mensagemErro(e))
    } finally {
      setSalvando(false)
    }
  }
  return (
    <Quadro titulo="Empresa">
      <Erro mensagem={erro()} />
      <Show when={ok()}>
        <p class="text-sm text-success">{ok()}</p>
      </Show>
      <div class="grid grid-cols-2 gap-3">
        <For each={CAMPOS_EMPRESA}>
          {(c) => (
            <Campo rotulo={c.rotulo}>
              <Input value={config()?.[c.chave] ?? ''} onInput={(e) => mutate({ ...(config() ?? {}), [c.chave]: e.currentTarget.value })} />
            </Campo>
          )}
        </For>
      </div>
      <div class="flex justify-end">
        <Button variant="accent" size="sm" disabled={salvando() || !config()} onClick={() => void salvar()}>{salvando() ? 'Salvando…' : 'Salvar'}</Button>
      </div>
    </Quadro>
  )
}

function Vhsys() {
  const [config, { refetch }] = createResource(() => api.vhsys.getConfig().catch(() => null))
  const [access, setAccess] = createSignal('')
  const [secret, setSecret] = createSignal('')
  const [teste, setTeste] = createSignal<VhsysTesteConexao | null>(null)
  const [resultado, setResultado] = createSignal<VhsysImportResult | null>(null)
  const [erro, setErro] = createSignal<string | null>(null)
  const [ocupado, setOcupado] = createSignal<'' | 'salvar' | 'testar' | 'importar'>('')
  const salvar = async () => {
    setErro(null)
    setTeste(null)
    if (!access().trim() || !secret().trim()) return setErro('Preencha o Access-Token e o Secret-Access-Token.')
    setOcupado('salvar')
    try {
      await api.vhsys.salvarCredenciais(access().trim(), secret().trim())
      setAccess('')
      setSecret('')
      void refetch()
    } catch (e) {
      setErro(mensagemErro(e))
    } finally {
      setOcupado('')
    }
  }
  const testar = async () => {
    setOcupado('testar')
    setTeste(null)
    try {
      setTeste(await api.vhsys.testarConexao())
    } catch (e) {
      setErro(mensagemErro(e))
    } finally {
      setOcupado('')
    }
  }
  const importar = async () => {
    if (!(await confirmar('Importar clientes do VHSys?', 'Clientes com o mesmo CNPJ/CPF serão atualizados.', { rotulo: 'Importar' }))) return
    setOcupado('importar')
    setResultado(null)
    setErro(null)
    try {
      setResultado(await api.vhsys.importarClientes())
    } catch (e) {
      setErro(mensagemErro(e))
    } finally {
      setOcupado('')
    }
  }
  return (
    <Quadro
      titulo="Integração VHSys"
      extra={
        <Show when={config()?.configurado} fallback={<Etiqueta>{config() === null ? 'sem conexão com o servidor' : 'não configurado'}</Etiqueta>}>
          <Etiqueta tom="sucesso">conectado {config()!.accessTokenMascarado}</Etiqueta>
        </Show>
      }
    >
      <p class="text-xs text-ink-subtle">
        Importa os clientes cadastrados no VHSys. Recurso <b>online</b> — exige internet. Os tokens ficam só no <b>servidor</b> (não vão para os computadores).
      </p>
      <Erro mensagem={erro()} />
      <div class="grid grid-cols-2 gap-3">
        <Campo rotulo="Access-Token">
          <Input type="password" autocomplete="off" value={access()} onInput={(e) => setAccess(e.currentTarget.value)} placeholder={config()?.configurado ? '•••• (já salvo)' : ''} />
        </Campo>
        <Campo rotulo="Secret-Access-Token">
          <Input type="password" autocomplete="off" value={secret()} onInput={(e) => setSecret(e.currentTarget.value)} placeholder={config()?.configurado ? '•••• (já salvo)' : ''} />
        </Campo>
      </div>
      <div class="flex flex-wrap gap-2">
        <Button variant="accent" size="sm" disabled={!!ocupado()} onClick={() => void salvar()}>{ocupado() === 'salvar' ? 'Salvando…' : 'Salvar credenciais'}</Button>
        <Button variant="secondary" size="sm" disabled={!!ocupado() || !config()?.configurado} onClick={() => void testar()}>{ocupado() === 'testar' ? 'Testando…' : 'Testar conexão'}</Button>
        <Button variant="secondary" size="sm" disabled={!!ocupado() || !config()?.configurado} onClick={() => void importar()}>{ocupado() === 'importar' ? 'Importando…' : 'Importar clientes'}</Button>
      </div>
      <Show when={teste()}>
        {(t) => <p class="rounded-md border px-3 py-2 text-sm" classList={{ 'border-success/40 bg-success/10 text-success': t().ok, 'border-failure/40 bg-failure/10 text-failure': !t().ok }}>{t().mensagem}</p>}
      </Show>
      <Show when={resultado()}>
        {(r) => (
          <div class="rounded-md border border-edge p-3 text-sm">
            <div class="text-ink-muted">
              Importação concluída: <span class="text-success">{r().importados} novo(s)</span>, <span class="text-accent">{r().atualizados} atualizado(s)</span>,{' '}
              <span class="text-warning">{r().ignorados} ignorado(s)</span> de {r().total}.
            </div>
            <Show when={r().erros.length > 0}>
              <ul class="mt-2 max-h-32 list-inside list-disc overflow-auto text-xs text-ink-subtle">
                <For each={r().erros.slice(0, 50)}>{(e) => <li>{e}</li>}</For>
              </ul>
            </Show>
            <div class="mt-1 text-xs text-ink-subtle">Os clientes chegam aos computadores pela sincronização.</div>
          </div>
        )}
      </Show>
    </Quadro>
  )
}
